import * as React from "react";
import { useEffect } from "react";
import { useParams } from "react-router";
import { ulid } from "ulid";

import { MessageInput } from "@/views/open-chat/components/messageInput";
import { OpenChatSidebar, OpenChatSidebarTab } from "./components/sidebar/sidebar";
import { PromptGeneratorContextProvider } from "@/components/feature/promptGenerator/contextProvider";
import { useGetMessagesQuery, useSendMessageMutation } from "@/api/open-chat";
import TopBarProgress from "react-topbar-progress-indicator";
import { Loader } from "@/components/ui/loader";
import { OpenChatMessageRow } from "./components/message/messageRow";
import { OpenChatWebSocketHandler } from "@/views/open-chat/components/webSocketHandler";
import { OpenChatMessage } from "@/types/open-chat";
import { OpenChatHeader } from "@/views/open-chat/components/header";
import { usePromptGeneratorContext } from "@/components/feature/promptGenerator/hooks";
import { usePromptLibraryContext } from "@/modules/promptLibrary/promptLibrary.context";
import { ModifyNewPromptLibraryItemDialog } from "@/modules/promptLibrary/components/modifyNewPromptLibraryItemDialog";

const initialMessage: OpenChatMessage = {
  id: "initial",
  content: "Welcome to open chat by pathfinder. The answers given are not document grounded.",
  type: "ai",
};

const emptyList: OpenChatMessage[] = [];

export const OpenChatWithParamsView: React.FC = () => (
  <PromptGeneratorContextProvider>
    <OpenChatWithParamsContent />
  </PromptGeneratorContextProvider>
);

export const OpenChatWithParamsContent: React.FC = () => {
  const { conversationId = "" } = useParams<{ conversationId: string }>();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { data: messages = emptyList, isLoading: isLoadingFirst, isSuccess } = useGetMessagesQuery(conversationId);
  const { createPromptLibraryModalState, closeCreatePromptLibraryModal } = usePromptLibraryContext();
  const [sendMessageMutation, { isLoading }] = useSendMessageMutation();
  const [tab, setTab] = React.useState<OpenChatSidebarTab>("prompt");

  const handleSendMessage = React.useCallback(
    async (message: string) => {
      await sendMessageMutation({
        conversationId,
        message,
        messageId: ulid(),
        prompt: "open-chat-style-prompt",
      }).unwrap();

      if (contentRef.current !== null) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    },
    [conversationId, sendMessageMutation],
  );

  const { promptToChat, sendPromptToChat } = usePromptGeneratorContext();
  React.useEffect(() => {
    if (promptToChat) {
      handleSendMessage(promptToChat);
      sendPromptToChat(null);
    }
  }, [promptToChat, sendPromptToChat, handleSendMessage]);

  const { promptToApply, subscribe: subscribeToSelectedPrompt, setPromptToApply } = usePromptLibraryContext();
  useEffect(() => {
    if (isLoadingFirst || !promptToApply || promptToApply.source !== "open_chat") {
      return;
    }

    handleSendMessage(promptToApply.prompt);
    setPromptToApply(null);
  }, [promptToApply, isLoadingFirst, handleSendMessage]);

  useEffect(() => {
    const unsubscribe = subscribeToSelectedPrompt(prompt => handleSendMessage(prompt));

    return () => unsubscribe();
  }, [subscribeToSelectedPrompt, handleSendMessage]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (isSuccess && contentRef.current !== null && messages.length) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [isSuccess, messages.length]);

  const onImprovePrompt = React.useCallback(() => {
    setTab("prompt");
  }, [setTab]);

  return (
    <>
      <OpenChatWebSocketHandler contentRef={contentRef} conversationId={conversationId} />
      <div className="w-75 margin-auto ml-auto mr-auto flex max-w-[150rem]">
        <div className="flex flex-grow overflow-hidden">
          <div className="mr-4 flex h-[95vh] w-[50%] min-w-[300px] flex-grow flex-col overflow-hidden rounded-xl shadow">
            <OpenChatHeader conversationId={conversationId} messages={messages} />

            <div className="flex flex-grow flex-col overflow-y-scroll bg-user-message p-4" ref={contentRef}>
              {isLoadingFirst && <TopBarProgress />}
              <OpenChatMessageRow allowImprovement={false} isImprovement={false} message={initialMessage} />
              {messages.map(message => (
                <OpenChatMessageRow
                  onImprovePrompt={onImprovePrompt}
                  isImprovement={false}
                  allowCopy={true}
                  allowImprovement={true}
                  key={message.id}
                  message={message}
                />
              ))}
              {isLoading && <Loader />}
            </div>
            <div className="flex flex-col bg-white p-4">
              <div className="mt-4">
                <MessageInput
                  onSendMessage={handleSendMessage}
                  isStreaming={false}
                  isReady={!isLoading && !isLoadingFirst}
                />
              </div>
            </div>
          </div>
          <OpenChatSidebar tab={tab} setTab={setTab} />

          {createPromptLibraryModalState && (
            <ModifyNewPromptLibraryItemDialog
              mode="add"
              isOpen={createPromptLibraryModalState !== null}
              setIsOpen={() => closeCreatePromptLibraryModal()}
              item={{
                prompt: createPromptLibraryModalState.prompt,
                source: createPromptLibraryModalState.source,
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};
